import type { CamelCase } from '../strings';

/** What type of connections can be created for this MCP server */
export enum MCPServerAccessAvailableFor {
  /** Users can only create connections to this MCP server with their personal credentials */
  PERSONAL = 'PERSONAL',
  /** Users can connect to this MCP server with shared credentials that the admin has configured */
  MANAGED = 'MANAGED'
}

export const MCPServerTransportMembers = [
  'SSE',
  'STREAMABLE_HTTP',
  'STDIO'
] as const;

export type MCPServerTransport = (typeof MCPServerTransportMembers)[number];

// spread list of types
export const MCPServerInstallTypes = {
  ...MCPServerAccessAvailableFor
} as const;

// a single type
export type MCPServerInstallType =
  (typeof MCPServerInstallTypes)[keyof typeof MCPServerInstallTypes];

export const MCPServerOriginMembers = ['NATOMA', 'BYO'] as const;

export type MCPServerOrigin = (typeof MCPServerOriginMembers)[number];

export const MCPClientTypeMembers = [
  'CHATGPT',
  'CLAUDE',
  'CLAUDE_CODE',
  'CLAUDE_WEB',
  'CLINE',
  'CODEX',
  'CORTEX_CODE',
  'CURSOR',
  'GEMINI',
  'KIRO',
  'LANGCHAIN',
  'MSFT_COPILOT',
  'OAI_PLAYGROUND',
  'OPENCLAW',
  'OPENCODE',
  'Q_DEVELOPER',
  'SUBCONSCIOUS',
  'VSCODE',
  'WINDSURF'
] as const;

export type MCPClientType = (typeof MCPClientTypeMembers)[number];

export const MCPServerSourceTypeMembers = [
  'REMOTE_URL', // http and https URLs supported
  'DYNAMIC_REMOTE_URL', // dynamic remote URL where the URL is constructed from admin-provided parameters
  'REPOSITORY', // a git repository
  'LOCAL_URL', // Locally running servers that use HTTP transport
  'LOCAL_PACKAGE', // Locally running MCP servers managed by the desktop application,
  'API_SPEC', // DEPRECATED - do not use. This was used for an old MCP server type that has been removed.
  'TUNNELED' // DEPRECATED - do not use. This was used for an old MCP server type that has been removed.
] as const;

export type MCPServerSourceType = (typeof MCPServerSourceTypeMembers)[number];

export interface IBaseServerInfoSource {
  sourceType: MCPServerSourceType;
}

/** Configuration for repository-based MCP servers */
export interface IRepositorySource extends IBaseServerInfoSource {
  sourceType: 'REPOSITORY';
  /** The url to the mcp server source code repo*/
  repositoryUrl: string;
  /** The MCP server template type */
  type: MCPServerTemplateType;
  /** The package type (npm, docker, pip) */
  packageType: 'npm' | 'docker' | 'pip';
  /** The package name (npm package name or docker image name) */
  packageName: string;
  /** Use latest commit from the branch */
  branchName?: string;
  /** Use specific commit SHA, or 'latest' */
  commitSha?: string;
  /** Working directory for build steps (defaults to root if not specified) */
  workingDirectory?: string;
  /** Map of environment variables for server deployment */
  envVars?: Map<string, string>;
}

/** Configuration for remotely hosted (sse | mcp) endpoint MCP servers that support ('streamable-http' | 'sse') */
export interface IRemoteUrlSource extends IBaseServerInfoSource {
  sourceType: 'REMOTE_URL';
  /** The MCP server URL. Either a static string or url builder function */
  mcpServerUrl: string | ((config: Map<string, string>) => string);
  /** The known transport for connecting to this MCP Server -  */
  transport?: MCPServerTransport;
}

// TODO(moore): Going to nuke this in the future and fold all DynamicRemoteUrl
// into IRemoteUrlSource
export interface IDynamicRemoteUrlSource extends IBaseServerInfoSource {
  sourceType: 'DYNAMIC_REMOTE_URL';
  /** The URL template with placeholders for parameters (e.g., https://{subdomain}.example.com) */
  mcpServerUrl: string;
  /** The known transport for connecting to this MCP Server */
  transport?: MCPServerTransport;
}

/*
 These MCP servers run on the user's machine over HTTP. Natoma reaches the MCP
 server using the local URL and transport, for example:
 Figma MCP server runs at http://127.0.0.1:3845 using STREAMABLE_HTTP.
 Natoma only connects to these servers and doesn't manage the server process or lifecycle.
*/
export interface ILocalUrlSource extends IBaseServerInfoSource {
  sourceType: 'LOCAL_URL';
  /** STREAMABLE_HTTP | SSE transports */
  transport: MCPServerTransport;
  /** Optional local URL to reach the MCP server when the port is known */
  url?: string;
}

/*
 These are the MCP servers which are locally run and managed by the desktop application
 Example:
 Playwright - The desktop application starts up the MCP Server with STDIO transport using
 npx -y @playwright/mcp@latest

 The desktop application manages the MCP server process and it's lifecycle
 Later on this could be extended to support other transports like SSE and Streamable HTTP
 In the future Natoma could host the binaries too, letting the desktop app download and run them automatically.
*/
export interface ILocalPackageSource extends IBaseServerInfoSource {
  sourceType: 'LOCAL_PACKAGE';
  /** Only STDIO transport for now */
  transport: 'STDIO';
  /** Serialized command function string invoked per session */
  commandFunction: string;
}

/**
 * Union type for all supported MCP registry source variants. This differs from the
 * exhaustive set of sources in the MCPServerTemplate model.
 */
export type MCPServerInfoSource =
  | IRepositorySource
  | IRemoteUrlSource
  | IDynamicRemoteUrlSource
  | ILocalUrlSource
  | ILocalPackageSource;

export const MCPServerAuthenticationTypeMembers = [
  'STATIC_CREDS',
  'OAUTH',
  'CLIENT_CREDENTIALS',
  'NONE'
] as const;

export type MCPServerAuthenticationType =
  (typeof MCPServerAuthenticationTypeMembers)[number];

export const MCPServerTemplateStatusMembers = ['ACTIVE', 'INACTIVE'] as const;

export type MCPServerTemplateStatus =
  (typeof MCPServerTemplateStatusMembers)[number];

export const MCPServerTemplateTypeMembers = [
  'COMMUNITY',
  'OFFICIAL',
  'NATOMA'
] as const;

export type MCPServerTemplateType =
  (typeof MCPServerTemplateTypeMembers)[number];

export const RepositoryPackageTypeMembers = ['npm', 'docker', 'pip'] as const;

export type RepositoryPackageType =
  (typeof RepositoryPackageTypeMembers)[number];

/**
 * Unique identifier for each MCP server template
 * These IDs are used to uniquely identify server templates across the system
 */
export const MCPServerIdMembers = [
  'AIRTABLE',
  'AIRWEAVE_SEARCH',
  'AMPLITUDE',
  'APIIRO',
  'ASANA',
  'ASHBY',
  'ASHBY_NATOMA',
  'ATLASSIAN_REMOTE',
  'ATLASSIAN',
  'AVALARA_DEV_DOCUMENTATION',
  'AVALARA_TAX_CONTENT',
  'AVATAX',
  'AWS_CLOUDWATCH',
  'AWS_COST_EXPLORER',
  'AWS_DOCUMENTATION',
  'AWS_IAM',
  'AZURE_DEVOPS',
  'BIGQUERY_REMOTE',
  'BOX',
  'BRAVE',
  'BRAZE',
  'BUILDKITE',
  'BULLHORN_CRM',
  'CHECKMARX',
  'CHEMBL',
  'CHROME_DEVTOOLS',
  'CIRCLECI',
  'CLICKUP',
  'CLOJURE',
  'CLOUDFLARE_AUDIT_LOGS',
  'CLOUDFLARE_WORKERS_OBSERVABILITY',
  'CLOUDZERO',
  'CONTEXT7_OAUTH',
  'CONTEXT7',
  'CROWDSTRIKE',
  'CUSTOM_API_SERVER',
  'CYERA_DATAPORT',
  'DAILYMED',
  'DATABRICKS',
  'DATADOG_COMMUNITY',
  'DATAFORSEO',
  'DATAHUB',
  'DOVETAIL',
  'DRAWIO',
  'DROPBOX',
  'EXCALIDRAW',
  'FETCH',
  'FIGMA_DESKTOP',
  'FIGMA',
  'FILESYSTEM',
  'FIRECRAWL',
  'FIREFLIES',
  'FRESHSERVICE',
  'FULLSTORY_LEXICON',
  'GENAI_TOOLBOX_BIGQUERY',
  'GENAI_TOOLBOX_LOOKER',
  'GENAI_TOOLBOX_ORACLEDB',
  'GENAI_TOOLBOX_POSTGRES',
  'GENAI_TOOLBOX',
  'GITHUB_OAUTH',
  'GITHUB_STDIO',
  'GITHUB',
  'GITLAB_REMOTE',
  'GITLAB',
  'GLEAN',
  'GONG',
  'GONG_COMMUNITY',
  'GRAFANA',
  'GOOGLE_CALENDAR',
  'GOOGLE_CALENDAR_OFFICIAL',
  'GOOGLE_CHAT',
  'GOOGLE_DOCS',
  'GOOGLE_DRIVE',
  'GOOGLE_DRIVE_OFFICIAL',
  'GOOGLE_FORMS',
  'GOOGLE_GMAIL',
  'GOOGLE_GMAIL_OFFICIAL',
  'GOOGLE_PEOPLE',
  'GOOGLE_SHEETS',
  'GOOGLE_SLIDES',
  'GOOGLE_WORKSPACE',
  'GRAFANA',
  'GRAPHITI',
  'GREENHOUSE',
  'CNA_GWRE_DEVELOPER_ASSISTANT',
  'CNA_JUTRO_DEVELOPER_ASSISTANT',
  'HARNESS',
  'HEROKU',
  'HONEYCOMB',
  'HUBSPOT',
  'INCIDENTIO',
  'INTERCOM',
  'INVIDEO',
  'ITERABLE',
  'JENKINS',
  'JFROG',
  'KUBERNETES',
  'LAUNCHDARKLY',
  'LAUNCHDARKLY_OFFICIAL',
  'LINEAR',
  'LINX',
  'LUCID',
  'MDAPI',
  'MICROSOFT_GRAPH_AND_ARM',
  'MICROSOFT365',
  'MIRO',
  'MIRO_OFFICIAL',
  'MIXPANEL',
  'MOCK_SERVER',
  'MONGODB',
  'MOTHERDUCK',
  'NEON',
  'NETSUITE',
  'NEW_RELIC',
  'NOTEBOOKLM',
  'NOTION',
  'OKTA_COMMUNITY',
  'OKTA_NATOMA',
  'PAGERDUTY',
  'PAYPAL',
  'PERPLEXITY_ASK',
  'PINECONE',
  'PLAID',
  'PLAYWRIGHT_LOCAL',
  'PLAYWRIGHT',
  'POWER_BI',
  'PRISMA',
  'PROMETHEUS',
  'PUBCHEM',
  'PUBMED',
  'REDIS',
  'RESEND',
  'SAILPOINT',
  'SALESFORCE_REMOTE',
  'SALESFORCE',
  'SCALR',
  'SELECT_STAR',
  'SEMGREP',
  'SENTINELONE',
  'SENTRY',
  'SEQUENTIALTHINKING',
  'SERVICENOW_REMOTE',
  'SERVICENOW',
  'SLACK_BETA',
  'SLACK_COMMUNITY',
  'SLACK_REMOTE',
  'SMARTBEAR',
  'SNOWFLAKE',
  'SONARQUBE',
  'SOURCEBOT',
  'SPACELIFT',
  'SPINACH',
  'SPLUNK',
  'SQUARE',
  'STAR_WARS',
  'STATSIG',
  'STRIPE',
  'SUPABASE',
  'SUPERNOVA',
  'TABLEAU',
  'TERADATA',
  'TIME',
  'TORII',
  'US_PTO',
  'UPWIND',
  'VEGA',
  'WEBFLOW',
  'WIZ',
  'WORKATO',
  'WRIKE',
  'ZAPIER',
  'ZOOM_REMOTE',
  'ZOOM',
  'ZOOMINFO'
] as const;

export type MCPServerId = (typeof MCPServerIdMembers)[number];

export type McpServerIdCamelCase = CamelCase<
  (typeof MCPServerIdMembers)[number]
>;

// NatomaClientType is used to identify the type of client making requests to Natoma services
export enum NatomaClientType {
  DESKTOP_APP = 'desktop-app',
  // CORE here is meant to be "AGENT", not changing now because all onprem agents docker tags will need an upgrade
  // TODO: Change CORE to AGENT
  CORE = 'core'
}

/**
 * Platform enum for desktop app and frontend
 */
export enum DesktopPlatform {
  WINDOWS = 'WINDOWS',
  MAC = 'MAC',
  LINUX = 'LINUX'
}

/**
 * Windows architecture enum for desktop app and frontend
 */
export enum WindowsArch {
  ARM64 = 'arm64',
  X64 = 'x64',
  IA32 = 'ia32'
}

export enum MacArch {
  UNIVERSAL = 'universal'
}

export type WindowsArchType = `${WindowsArch}`; // 'arm64' | 'x64' | 'ia32'
export type MacArchType = `${MacArch}`; // 'universal'
export interface ResolvedPlatform {
  platform: DesktopPlatform;
  arch: WindowsArchType | MacArchType;
}

export type MCPServerInfoConfigProperty = {
  type: string;
  description?: string;
  sensitive: boolean;
  default?: string;
  isUrlParameter?: boolean;
};

/**
 * Defines the schema for MCP server configuration, including required properties and their details.
 * This schema is used to validate the configuration provided for MCP servers, ensuring that all necessary
 * properties are included and correctly formatted based on the defined types and requirements.
 * @field {string[]} required - An array of strings that must be a subset of the keys in the `properties` object,
 * indicating which configuration properties are mandatory.
 * @field {Record<string, {@link MCPServerInfoConfigProperty}>} properties - A record mapping configuration property names to their details,
 */
export type IMCPServerInfoConfigSchema = {
  /**
   * List of required configuration properties.
   * Must be a subset of the keys in the `properties` object.
   */
  required: string[];
  /* Map of configuration properties */
  properties: Record<string, MCPServerInfoConfigProperty>;
};

/**
 * Defines a single credential configuration schema. This credential config outlines the details
 * that we need to capture from end users to configure an MCP server
 */
export type MCPServerInfoCredential = MCPServerInfoConfigProperty & {
  // Env var key name for the cred for reading off process.env
  envKey?: string;
};

/**
 * Defines authentication requirements and possible scope selections on the MCP server.
 * Each server definition will need to outline the required credentials to connect and outline
 * them in `creds`
 * @field supportedScopes - list of supported scopes on this MCP server
 * @field requiredScopes - list of required scopes for this server to function
 * @field scopeDelimiter - override on scope delimiter, default to space
 * @field creds - Individual credential properties that need to be captured to connect to MCP server
 */
export type MCPServerInfoAuthConfig = {
  // List of prefetched scopes that we surface to admins during configuration-time
  supportedScopes?: string[];
  // List of scopes that are required for this server to function
  requiredScopes?: string[];
  // RFC 6749 says "space-delimited, case-sensitive" but not always followed.
  // Default to space
  scopeDelimiter?: string;
  // Properties that we need to capture from end-user to configure an MCP server
  creds: Record<string, MCPServerInfoCredential>;
};

interface IMCPServerInstructions {
  serverSetupInstructions?: string; // markdown format
  installSetupInstructions?: string; // markdown format
}

/**
 * Defines information about a Natoma registry server. BYO servers use
 * a legacy MCPServerTemplate schema embedded on the MCPServerDocument.
 */
export interface MCPServerInfo {
  /** Unique identifier for the MCP server */
  serverId: MCPServerId;
  /** User-visible permalink slug */
  slug: string;
  /** User-visible display name of the MCP server */
  name: string;
  description?: string;
  authenticationType: MCPServerAuthenticationType;
  source: MCPServerInfoSource;
  serverConfig: IMCPServerInfoConfigSchema;
  installConfig: IMCPServerInfoConfigSchema;
  /** Any auth configs are stored here, e.g. supported scopes, clientId/secret */
  authConfig?: MCPServerInfoAuthConfig;
  /**
   * Optional human-friendly setup instructions in markdown format
   */
  instructions?: IMCPServerInstructions;
  /** Object containing useful annotations about servers */
  annotations?: Partial<{
    /** An indication for a registry server that Natoma "knows" will require a pre-registered OAuth Client */
    hintPreRegistered: boolean;
  }>;
  /** When true, this server has been deprecated and must not be offered for new connections */
  deprecated?: boolean;
}
